export type KioskIncidentLifecycle = "opened" | "escalated" | "recovered";

export type ActiveKioskIncident = {
  id: string;
  code: string;
  operation: string;
  openedAt: number;
  occurrences: number;
  escalated: boolean;
};

const INCIDENTS_KEY = "kiosk_active_incidents_v1";

function readActiveIncidents(): ActiveKioskIncident[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(INCIDENTS_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((incident): incident is ActiveKioskIncident => {
      if (!incident || typeof incident !== "object") return false;
      const item = incident as Partial<ActiveKioskIncident>;
      return (
        typeof item.id === "string" &&
        typeof item.code === "string" &&
        typeof item.operation === "string" &&
        typeof item.openedAt === "number" &&
        typeof item.occurrences === "number" &&
        typeof item.escalated === "boolean"
      );
    });
  } catch {
    return [];
  }
}
function writeActiveIncidents(incidents: ActiveKioskIncident[]) {
  try {
    localStorage.setItem(INCIDENTS_KEY, JSON.stringify(incidents));
  } catch {
    // Analytics must never crash the kiosk when Safari storage is unavailable.
  }
}

function createIncidentId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `incident-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function openKioskIncident(code: string, operation: string) {
  const incidents = readActiveIncidents();
  const active = incidents.find(
    (incident) => incident.code === code && incident.operation === operation,
  );
  if (active) {
    active.occurrences += 1;
    writeActiveIncidents(incidents);
    return { incident: active, created: false } as const;
  }

  const incident: ActiveKioskIncident = {
    id: createIncidentId(),
    code,
    operation,
    openedAt: Date.now(),
    occurrences: 1,
    escalated: false,
  };
  writeActiveIncidents([...incidents, incident]);
  return { incident, created: true } as const;
}

export function escalateKioskIncident(id: string) {
  const incidents = readActiveIncidents();
  const incident = incidents.find((item) => item.id === id);
  if (!incident || incident.escalated) return null;
  incident.escalated = true;
  writeActiveIncidents(incidents);
  return incident;
}

export function recoverKioskIncidents(code?: string) {
  const incidents = readActiveIncidents();
  const recovered = code
    ? incidents.filter((incident) => incident.code === code)
    : incidents;
  if (recovered.length === 0) return [];
  writeActiveIncidents(
    code ? incidents.filter((incident) => incident.code !== code) : [],
  );
  return recovered.map((incident) => ({
    ...incident,
    durationMs: Math.max(0, Date.now() - incident.openedAt),
  }));
}

export function recoverKioskIncidentById(id: string) {
  const incidents = readActiveIncidents();
  const recovered = incidents.find((incident) => incident.id === id);
  if (!recovered) return null;

  writeActiveIncidents(incidents.filter((incident) => incident.id !== id));
  return {
    ...recovered,
    durationMs: Math.max(0, Date.now() - recovered.openedAt),
  };
}
